import { useCallback, useEffect, useMemo } from 'react'

import { useFilterManager } from '@webapp/components/FilterManager/useFilterManager'
import { toast } from '@webapp/components/Notification'
import { useCurrencySelect } from '@webapp/core/components/CurrencySelect/useCurrencySelect'
import { type PrimaryCategory } from '@webapp/core/entities/internal/PrimaryCategory'
import { type SecondaryCategory } from '@webapp/core/entities/internal/SecondaryCategory'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import { type ApprovedExpenseItemListParams } from '@webapp/modules/expense/data-access/types/ApprovedExpenseItemListParams'
import messages from './messages'
import { useQueryApprovedExpenseItems } from '../../models/useQueryApprovedExpenseItems'

// Minimal reference — only the fields the filter UI renders (the resolved
// /api/v3/users/{id}/basic/ payload satisfies it)
export interface RequesterFilterReference {
  id: number
  firstName: string
  lastName: string
}

export interface ApprovedExpenseItemListFilterValues {
  location?: PrimaryCategory
  department?: SecondaryCategory
  requester?: RequesterFilterReference
  order_created_date_0?: string
  order_created_date_1?: string
  approved_datetime_0?: string
  approved_datetime_1?: string
}

const toNumberParam = (value: unknown): number | undefined =>
  value !== undefined && value !== null && value !== ''
    ? Number(value)
    : undefined

const toStringParam = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined

export const useApprovedExpenseItemListPresenter = () => {
  const intl = useProcurifyIntl()

  const expenseDetailsMigrationEnabled = useFeatureFlag(
    'ARCH_531_MIGRATION_EXPENSE_DETAILS'
  )

  const formatMedia = useFormatMedia()

  // Cached currency lookup shared with CurrencySelect — the entity carries
  // only currencyId; the Total Amount cell needs the code (BHV-012)
  const {
    data: { currencies },
  } = useCurrencySelect()

  // Filter state persists to URL query params (BHV-008); wire param names
  // match the Angular list (location/department/requester/date pairs).
  const filterManager = useFilterManager<ApprovedExpenseItemListFilterValues>({
    params: [
      'location',
      'department',
      'requester:user',
      'order_created_date_0',
      'order_created_date_1',
      'approved_datetime_0',
      'approved_datetime_1',
    ],
    resolve: {
      order_created_date_0: (value) => value,
      order_created_date_1: (value) => value,
      approved_datetime_0: (value) => value,
      approved_datetime_1: (value) => value,
    },
    formatLabels: {
      location: (location) => location?.name ?? '',
      department: (department) => department?.name ?? '',
      requester: (requester) =>
        requester ? `${requester.firstName} ${requester.lastName}` : '',
    },
    syncUrlParams: true,
  })

  const { queryParams } = filterManager

  const listParams = useMemo<ApprovedExpenseItemListParams>(
    () => ({
      primaryCategoryId: toNumberParam(queryParams.location),
      secondaryCategoryId: toNumberParam(queryParams.department),
      requesterId: toNumberParam(queryParams.requester),
      submittedDateStart: toStringParam(queryParams.order_created_date_0),
      submittedDateEnd: toStringParam(queryParams.order_created_date_1),
      approvedDateStart: toStringParam(queryParams.approved_datetime_0),
      approvedDateEnd: toStringParam(queryParams.approved_datetime_1),
    }),
    [queryParams]
  )

  const { items, pagination, isFetching, error } =
    useQueryApprovedExpenseItems(listParams)

  useEffect(() => {
    if (error) {
      toast.error({
        title: intl.formatMessage(messages.fetchErrorTitle),
        children:
          getMessageFromError(error) ??
          intl.formatMessage(messages.fetchErrorMessage),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  // RFE link target per ARCH_531_MIGRATION_EXPENSE_DETAILS (BHV-011);
  // BridgeLink renders /#/ targets as plain anchors
  const buildExpenseDetailUrl = useCallback(
    (uuid: string) =>
      expenseDetailsMigrationEnabled
        ? `/expenses/${uuid}`
        : `/#/expenses/${uuid}`,
    [expenseDetailsMigrationEnabled]
  )

  const resolveAttachmentUrl = useCallback(
    (attachment: string) => formatMedia(attachment),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const resolveCurrencyCode = useCallback(
    (currencyId: number) =>
      currencies.find((currency) => currency.id === currencyId)?.name,
    [currencies]
  )

  return {
    data: {
      items,
      pagination,
    },
    states: {
      isFetching,
    },
    methods: {
      buildExpenseDetailUrl,
      resolveAttachmentUrl,
      resolveCurrencyCode,
    },
    filterManager,
  }
}
